import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonService } from '../../../api/services/common.service';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FORM_ID } from '../../../helpers/form-id.enum';

@Component({
  selector: 'app-filter-dropdown',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './filter-dropdown.component.html',
  styleUrl: './filter-dropdown.component.css',
})
export class FilterDropdownComponent implements OnInit, OnDestroy, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() formFields: any[] = [];
  @Input() triggerLabel: string = 'Filter';
  @Input() fields: any;
  @Input() isEmbedded: boolean = true;
  @Input() formId: any;

  @Output() apply = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<void>();

  isOpen: boolean = false;
  activeField: any = null;
  endpoint = environment.api_endpoint;

  // Data caches
  optionsData: { [fieldName: string]: any[] } = {};
  allOptionsData: { [fieldName: string]: any[] } = {};
  loadingFields: { [fieldName: string]: boolean } = {};
  loadingMoreFields: { [fieldName: string]: boolean } = {};

  // Search terms
  searchTerms: { [fieldName: string]: string } = {};

  // Pagination states
  currentPages: { [fieldName: string]: number } = {};
  hasMoreData: { [fieldName: string]: boolean } = {};
  limit = 20;

  // Date range states
  selectedDatePreset: 'today' | 'last7' | 'last30' | 'custom' | null = null;
  startDateValue: string = '';
  endDateValue: string = '';

  private routerSub!: Subscription;
  private openAnimationFrameId: number | null = null;

  @ViewChild('dropdownPanel', { static: false }) dropdownPanel!: ElementRef;
  @ViewChild('triggerButton', { static: false }) triggerButton!: ElementRef;

  constructor(
    private commonService: CommonService,
    private elementRef: ElementRef,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.ensureFieldConfigs();
    if (this.isEmbedded) {
      this.isOpen = true;
    }
    if (this.formFields && this.formFields.length > 0) {
      this.activeField = this.formFields[0];
      if (this.isEmbedded) {
        this.selectField(this.activeField);
      }
    }

    if (!this.isEmbedded) {
      document.addEventListener('click', this.handleClickOutside, true);
      window.addEventListener('scroll', this.handleScroll, true);

      this.routerSub = this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => {
          this.closeDropdown();
          this.cd.detectChanges();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.ensureFieldConfigs();
    if (
      changes['formFields'] &&
      this.formFields &&
      this.formFields.length > 0
    ) {
      const activeExists =
        this.activeField &&
        this.formFields.some((f) => f.fieldName === this.activeField.fieldName);
      if (!activeExists) {
        this.activeField = this.formFields[0];
        if (this.isEmbedded) {
          this.selectField(this.activeField);
        }
      } else {
        this.activeField = this.formFields.find(
          (f) => f.fieldName === this.activeField.fieldName,
        );
      }
    }
  }

  ngOnDestroy() {
    if (!this.isEmbedded) {
      this.closeDropdown();
      this.routerSub?.unsubscribe();
      document.removeEventListener('click', this.handleClickOutside, true);
      window.removeEventListener('scroll', this.handleScroll, true);
    }
  }

  ensureFieldConfigs() {
    if (!this.formFields || !Array.isArray(this.formFields)) return;
    this.formFields.forEach((f: any) => {
      if (this.isImpactFieldOnActionableFilter(f)) {
        f.multiselect = true;
      }
      if (f.fieldName === 'assetCategories') {
        if (!f.options) f.options = {};
        if (!f.options.source) f.options.source = {};
        f.options.source.formID = '6a97f32d6dbb4bbc078d471e';
        f.options.isStatic = false;
      }
    });

    const dateRangeField = this.formFields.find((f: any) =>
      this.isDateRangeField(f),
    );
    if (dateRangeField && this.formGroup) {
      const fieldName = dateRangeField.fieldName || 'dateRange';
      if (!this.formGroup.contains(fieldName)) {
        this.formGroup.addControl(fieldName, new FormControl(null));
      }
      if (!this.formGroup.contains('startDate')) {
        this.formGroup.addControl('startDate', new FormControl(null));
      }
      if (!this.formGroup.contains('endDate')) {
        this.formGroup.addControl('endDate', new FormControl(null));
      }
      this.syncDateRangeFromFormGroup();
    }
  }

  isImpactFieldOnActionableFilter(field: any): boolean {
    if (!field || field.fieldName !== 'impact') return false;
    const formId =
      this.formId ||
      field.formId ||
      field.options?.formId ||
      this.fields?.formId ||
      this.formFields?.find((f: any) => f?.formId)?.formId;
    return (
      formId === '68d3e4b82309babcadb8fc17' ||
      formId === FORM_ID.ACTIONABLE_FILTER
    );
  }

  isFieldMultiselect(field: any): boolean {
    if (!field) return false;
    if (this.isImpactFieldOnActionableFilter(field)) {
      return true;
    }
    return !!field.multiselect;
  }

  getControl(fieldName: string) {
    if (!this.formGroup) return null;
    return this.formGroup.get(fieldName) || this.formGroup.controls[fieldName];
  }

  toggleDropdown(event: MouseEvent) {
    if (this.isEmbedded) return;
    event.stopPropagation();
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (this.isEmbedded) {
      this.isOpen = true;
      return;
    }
    this.isOpen = true;
    if (this.activeField) {
      this.selectField(this.activeField);
    }

    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
    }

    this.openAnimationFrameId = requestAnimationFrame(() => {
      this.openAnimationFrameId = null;
      if (!this.isOpen) return;

      const panel = this.dropdownPanel?.nativeElement;
      if (panel) {
        if (panel.parentNode !== document.body) {
          document.body.appendChild(panel);
        }
        panel.style.display = 'block';
        panel.style.position = 'absolute';
        this.positionDropdown();
      }
    });
  }

  positionDropdown() {
    const panel = this.dropdownPanel?.nativeElement;
    const trigger = this.triggerButton?.nativeElement;
    if (!panel || !trigger || !this.isOpen) return;

    this.cd.detectChanges();
    const rect = trigger.getBoundingClientRect();
    const panelWidth = 480; // left col ~180px, right col ~300px
    panel.style.width = panelWidth + 'px';
    panel.style.left =
      Math.min(
        rect.left + window.scrollX,
        window.innerWidth - panelWidth - 20,
      ) + 'px';

    // Simple vertical positioning below trigger (can add above positioning if scroll space is small)
    panel.style.top = rect.bottom + window.scrollY + 8 + 'px';
  }

  closeDropdown() {
    if (this.isEmbedded) return;
    this.isOpen = false;
    const panel = this.dropdownPanel?.nativeElement;
    if (panel) {
      panel.style.display = 'none';
      if (panel.parentNode === document.body) {
        document.body.removeChild(panel);
      }
    }
    if (this.openAnimationFrameId !== null) {
      cancelAnimationFrame(this.openAnimationFrameId);
      this.openAnimationFrameId = null;
    }
  }

  handleScroll = (event: Event) => {
    if (!this.isOpen) return;
    const panel = this.dropdownPanel?.nativeElement;
    if (panel?.contains(event.target as Node)) return;
    this.closeDropdown();
  };

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.handleClickOutside(event);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (!this.isOpen) return;

    const panel = this.dropdownPanel?.nativeElement;
    const trigger = this.triggerButton?.nativeElement;

    if (
      panel?.contains(event.target as Node) ||
      trigger?.contains(event.target as Node)
    ) {
      return;
    }
    this.closeDropdown();
  };

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  selectField(field: any) {
    if (field?.fieldName === 'assetCategories') {
      if (!field.options) field.options = {};
      if (!field.options.source) field.options.source = {};
      field.options.source.formID = '6a97f32d6dbb4bbc078d471e';
      field.options.isStatic = false;
    }

    if (this.activeField && this.activeField.fieldName !== field.fieldName) {
      const prevField = this.activeField;
      const prevFieldName = prevField.fieldName;

      if (this.searchTerms[prevFieldName]) {
        this.searchTerms[prevFieldName] = '';
        this.filterFieldOptions(prevField);

        // If the previous field was dynamic, reload its full unfiltered options
        if (!prevField.options?.isStatic && !this.isDateRangeField(prevField)) {
          this.loadFieldOptions(prevField, '');
        }
      }
    }

    this.activeField = field;
    const fieldName = field.fieldName;

    if (this.isDateRangeField(field)) {
      this.syncDateRangeFromFormGroup();
      if (!this.startDateValue && !this.endDateValue) {
        this.applyDatePreset('today');
      }
      return;
    }

    // Fetch data if not already cached
    if (!this.optionsData[fieldName]) {
      this.currentPages[fieldName] = 1;
      this.hasMoreData[fieldName] = true;
      this.loadFieldOptions(field);
    }
  }

  loadFieldOptions(
    field: any,
    query: string = '',
    isLoadMore: boolean = false,
  ) {
    const fieldName = field.fieldName;
    if (fieldName === 'assetCategories') {
      if (!field.options) field.options = {};
      if (!field.options.source) field.options.source = {};
      field.options.source.formID = '6a97f32d6dbb4bbc078d471e';
      field.options.isStatic = false;
    }
    if (field.options?.isStatic) {
      let staticOpts = field.options.staticOptions || [];
      if (fieldName === 'status') {
        staticOpts = staticOpts.map((opt: any) => {
          if (
            opt &&
            (opt.id === 'ARCHIEVE' ||
              opt.value === 'ARCHIEVE' ||
              opt.text === 'ARCHIEVE')
          ) {
            return { ...opt, id: 'ARCHIVE', text: 'ARCHIVE', value: 'ARCHIVE' };
          }
          return opt;
        });
      }
      if (fieldName === 'userType') {
        staticOpts = staticOpts.map((opt: any) => {
          if (
            opt &&
            (opt.id === 'Members' ||
              opt.value === 'Members' ||
              opt.text === 'Members')
          ) {
            return { ...opt, id: 'Member', text: 'Member', value: 'Member' };
          }
          return opt;
        });
      }
      this.optionsData[fieldName] = [...staticOpts];
      this.allOptionsData[fieldName] = [...staticOpts];
      this.filterFieldOptions(field);
    } else {
      if (isLoadMore) {
        this.loadingMoreFields[fieldName] = true;
      } else {
        this.loadingFields[fieldName] = true;
      }
      const formId =
        fieldName === 'assetCategories'
          ? '6a97f32d6dbb4bbc078d471e'
          : field.options?.source?.formID;
      const page = this.currentPages[fieldName] || 1;

      const params: any = { limit: this.limit, page: page };
      if (query) {
        params.query = query;
      }
      if (formId === '68cbc4047cc30f5af305d13b') {
        params.sort = 'domain';
      }
      if (formId === '6878d3800453556c08933e90') {
        params.status = 'ACTIVE';
        params.sort = 'name';
      }

      this.commonService.loadListWithOptions(formId, params).subscribe({
        next: (res: any) => {
          const records = res?.data?.records || [];

          if (page === 1) {
            this.optionsData[fieldName] = records;
            this.allOptionsData[fieldName] = records;
          } else {
            this.optionsData[fieldName] = [
              ...(this.optionsData[fieldName] || []),
              ...records,
            ];
            this.allOptionsData[fieldName] = [
              ...(this.allOptionsData[fieldName] || []),
              ...records,
            ];
          }

          if (records.length < this.limit) {
            this.hasMoreData[fieldName] = false;
          }

          this.filterFieldOptions(field);
          if (isLoadMore) {
            this.loadingMoreFields[fieldName] = false;
          } else {
            this.loadingFields[fieldName] = false;
          }
          this.cd.detectChanges();
        },
        error: () => {
          if (page === 1) {
            this.optionsData[fieldName] = [];
            this.allOptionsData[fieldName] = [];
          }
          if (isLoadMore) {
            this.loadingMoreFields[fieldName] = false;
          } else {
            this.loadingFields[fieldName] = false;
          }
          this.cd.detectChanges();
        },
      });
    }
  }

  onScroll(event: Event) {
    if (!this.activeField || this.activeField.options?.isStatic === true)
      return;

    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
      this.loadMore();
    }
  }

  loadMore() {
    if (!this.activeField) return;
    const fieldName = this.activeField.fieldName;
    if (
      this.loadingMoreFields[fieldName] ||
      this.loadingFields[fieldName] ||
      this.hasMoreData[fieldName] === false
    )
      return;

    this.currentPages[fieldName] = (this.currentPages[fieldName] || 1) + 1;
    const query = this.searchTerms[fieldName] || '';
    this.loadFieldOptions(this.activeField, query, true);
  }

  filterFieldOptions(field: any) {
    const fieldName = field.fieldName;
    const term = (this.searchTerms[fieldName] || '').toLowerCase().trim();
    const allOpts = this.allOptionsData[fieldName] || [];

    if (!term) {
      this.optionsData[fieldName] = [...allOpts];
    } else {
      this.optionsData[fieldName] = allOpts.filter((opt) => {
        const text = this.getOptionText(field, opt).toLowerCase();
        const email = (opt?.email || '').toLowerCase();
        const domain = (opt?.domain || '').toLowerCase();
        const mappingHeader = (opt?.mappingColumnHeader || '').toLowerCase();
        const assetCategory = (opt?.assetCategory || '').toLowerCase();
        const assetSubCategory = (opt?.assetSubCategory || '').toLowerCase();
        const assetType = (opt?.assetType || '').toLowerCase();
        return (
          text.includes(term) ||
          email.includes(term) ||
          domain.includes(term) ||
          mappingHeader.includes(term) ||
          assetCategory.includes(term) ||
          assetSubCategory.includes(term) ||
          assetType.includes(term)
        );
      });
    }
  }

  onSearchChange(field: any, value: string) {
    const fieldName = field.fieldName;
    this.searchTerms[fieldName] = value;
    this.filterFieldOptions(field);

    if (!field.options?.isStatic) {
      const term = value.trim();
      if (term.length >= 3 || term.length === 0) {
        this.currentPages[fieldName] = 1;
        this.hasMoreData[fieldName] = true;
        this.loadFieldOptions(field, term);
      }
    }
  }

  getOptionText(field: any, option: any): string {
    if (!option) return '';
    if (typeof option === 'string') {
      if (field?.fieldName === 'technology') {
        const lower = option.toLowerCase();
        if (lower === '' || lower === 'manual') return 'Manual';
        if (lower === '!=' || lower === 'automated') return 'Automated';
        if (lower === 'all') return 'All';
      }
      return option;
    }

    const formId = field?.options?.source?.formID;
    if (formId === '6a0ed7f32db4b8fda5f347a6') {
      return option.role || '';
    }

    if (formId === '6a3d163923bf07d3d695d993') {
      return option.shortName || option.fullName || option.name || '';
    }

    if (formId === '6a97f3fe6dbb4bbc078d4723') {
      return option.assetSubCategory || option.text || option.name || '';
    }

    if (
      formId === '6a97f32d6dbb4bbc078d471e' ||
      field?.fieldName === 'assetCategories'
    ) {
      return option.assetCategory || option.text || option.name || '';
    }

    if (formId === '6a97f5156dbb4bbc078d4729') {
      return option.assetType || option.text || option.name || '';
    }

    if (option.mappingColumnHeader) return option.mappingColumnHeader;
    if (option.text) return option.text;
    if (option.name) return option.name;
    if (option.assetCategory) return option.assetCategory;
    if (option.assetSubCategory) return option.assetSubCategory;
    if (option.assetType) return option.assetType;
    if (option.domain) {
      return option.fullName
        ? `${option.domain} (${option.fullName})`
        : option.domain;
    }
    if (option.displayName) return option.displayName;
    return option.label || '';
  }

  getOptionValue(field: any, option: any): any {
    if (!option && option !== '') return null;

    const fieldName = field.fieldName;

    if (fieldName === 'technology') {
      const optStr = (
        typeof option === 'string'
          ? option
          : (option?.id ?? option?.value ?? option?.text ?? '')
      )
        .toString()
        .toLowerCase();

      if (optStr === 'manual' || optStr === '') return '';
      if (optStr === 'automated' || optStr === '!=') return '!=';
      if (optStr === 'all') return 'all';
      return optStr;
    }

    if (typeof option === 'string') return option;

    const formId = field.options?.source?.formID;

    if (formId === '6a0ed7f32db4b8fda5f347a6') {
      return option.role ?? option._id;
    }

    if (formId === '6a0edc492db4b8fda5f347b7' || fieldName === 'controls') {
      return (
        option?.text ||
        option?.name ||
        option?.label ||
        option?.value ||
        option?._id
      );
    }

    if (formId === '6a3d163923bf07d3d695d993') {
      return (
        option?.shortName || option?.fullName || option?.name || option?._id
      );
    }

    if (formId === '69c5054ecc0290f4b6634830') {
      return option?.name;
    }

    if (formId === '6a3e48c9f6046f956454a18e') {
      return option?.name;
    }

    if (formId === '6a97f3fe6dbb4bbc078d4723') {
      return (
        option?.assetSubCategory ??
        option?.text ??
        option?.name ??
        option?._id
      );
    }

    if (
      formId === '6a97f32d6dbb4bbc078d471e' ||
      fieldName === 'assetCategories'
    ) {
      return (
        option?.assetCategory ??
        option?.text ??
        option?.name ??
        option?._id
      );
    }

    if (formId === '6a97f5156dbb4bbc078d4729') {
      return (
        option?.assetType ??
        option?.text ??
        option?.name ??
        option?._id
      );
    }

    switch (fieldName) {
      case 'status':
        if (!option?.id && !option?._id) {
          return option?.text || option?.value || option?.name;
        }
        return option?.id ?? option?._id ?? option?.value ?? option?.text;

      case 'invite':
      case 'contactType':
        return option?.text || option?.value || option?.name || option?.id || option?._id;

      case 'category':
        return option?.value ?? option?.id ?? option?._id;

      case 'domains':
      case 'domainSubdomainMappings':
        return option?.domain ?? option?.value ?? option?.id;

      case 'owners':
      case 'assigned':
        return option?.email ?? option?.id ?? option?._id;

      case 'department':
        return option.name ?? option?.id ?? option?.department;

      default:
        return (
          option?.assetSubCategory ??
          option?.assetCategory ??
          option?.assetType ??
          option?.email ??
          option?.domain ??
          option?.id ??
          option?.value ??
          option?.role ??
          option?.mappingColumnHeader ??
          option?._id ??
          option?.name
        );
    }
  }

  isSelected(field: any, option: any): boolean {
    const control = this.getControl(field.fieldName);
    if (!control) return false;

    if (field.fieldName === 'technology') {
      let ctrlVal = control.value;
      if (Array.isArray(ctrlVal)) {
        ctrlVal = ctrlVal.length > 0 ? ctrlVal[0] : null;
      }
      if (ctrlVal === null || ctrlVal === undefined) return false;

      const optVal = this.getOptionValue(field, option);
      if (ctrlVal === optVal) return true;

      const rawOptId = (option?.id ?? option?.value ?? option?.text ?? '')
        .toString()
        .toLowerCase();
      if (ctrlVal.toString().toLowerCase() === rawOptId) return true;

      return false;
    }

    if (!control.value) return false;

    const value = this.getOptionValue(field, option);
    if (this.isFieldMultiselect(field)) {
      if (Array.isArray(control.value)) {
        return control.value.includes(value);
      }
      return control.value === value;
    }
    return control.value === value;
  }

  toggleOption(field: any, option: any) {
    const control = this.getControl(field.fieldName);
    if (!control) return;

    const value = this.getOptionValue(field, option);

    if (field.fieldName === 'technology') {
      if (this.isSelected(field, option)) {
        control.setValue(null, { emitEvent: true });
      } else {
        control.setValue(value, { emitEvent: true });
      }
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
      this.cd.detectChanges();
      return;
    }

    if (this.isFieldMultiselect(field)) {
      let currentValues = control.value || [];
      if (!Array.isArray(currentValues)) {
        currentValues = [currentValues];
      }

      if (currentValues.includes(value)) {
        currentValues = currentValues.filter((v: any) => v !== value);
      } else {
        currentValues = [...currentValues, value];
      }

      control.setValue(
        currentValues.length > 0 ? currentValues : null,
        { emitEvent: true },
      );
    } else {
      if (this.isSelected(field, option)) {
        control.setValue(null, { emitEvent: true });
      } else {
        control.setValue(value, { emitEvent: true });
      }
    }

    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();

    this.cd.detectChanges();
  }

  getSelectedCount(field: any): number {
    if (this.isDateRangeField(field)) {
      return this.startDateValue && this.endDateValue ? 1 : 0;
    }
    const control = this.getControl(field.fieldName);
    if (!control) return 0;
    if (field.fieldName === 'technology') {
      const val = Array.isArray(control.value)
        ? (control.value.length > 0 ? control.value[0] : null)
        : control.value;
      if (val === null || val === undefined || val === 'all') return 0;
      return 1;
    }
    if (!control.value) return 0;
    if (Array.isArray(control.value)) return control.value.length;
    return 1;
  }

  getTotalSelectedCount(): number {
    let total = 0;
    for (const field of this.formFields) {
      total += this.getSelectedCount(field);
    }
    return total;
  }

  clearAllFilters() {
    this.clearDateRange();
    for (const field of this.formFields) {
      const control = this.getControl(field.fieldName);
      if (control) {
        control.setValue(null, { emitEvent: true });
        control.markAsDirty();
        control.updateValueAndValidity();
      }
      this.searchTerms[field.fieldName] = '';
      this.filterFieldOptions(field);
    }
    this.cd.detectChanges();
  }

  clearFieldFilters(field: any) {
    if (!field) return;
    if (this.isDateRangeField(field)) {
      this.clearDateRange();
      return;
    }
    const control = this.getControl(field.fieldName);
    if (control) {
      control.setValue(null, { emitEvent: true });
      control.markAsDirty();
      control.updateValueAndValidity();
    }
    this.cd.detectChanges();
  }

  // Date Range Helpers
  isDateRangeField(field: any): boolean {
    if (!field) return false;
    const type = (field.fieldType || '').toLowerCase().replace(/[-_]/g, '');
    if (type === 'daterange') return true;
    const name = (field.fieldName || '').toLowerCase().replace(/[-_]/g, '');
    return !field.fieldType && name === 'daterange';
  }

  getDateRangeFieldName(): string {
    if (this.activeField && this.isDateRangeField(this.activeField)) {
      return this.activeField.fieldName || 'dateRange';
    }
    const dateRangeField = this.formFields?.find((f: any) =>
      this.isDateRangeField(f),
    );
    return dateRangeField?.fieldName || 'dateRange';
  }

  formatIsoDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(isoStr: string): string {
    if (!isoStr) return '';
    const parts = isoStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, monthIndex, day);
      const month = d.toLocaleString('en-US', { month: 'short' });
      return `${month} ${day}, ${year}`;
    }
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${month} ${d.getDate()}, ${d.getFullYear()}`;
  }

  applyDatePreset(preset: 'today' | 'last7' | 'last30' | 'custom') {
    this.selectedDatePreset = preset;
    const today = new Date();
    const todayIso = this.formatIsoDate(today);

    if (preset === 'today') {
      this.startDateValue = todayIso;
      this.endDateValue = todayIso;
    } else if (preset === 'last7') {
      const last7 = new Date();
      last7.setDate(today.getDate() - 7);
      this.startDateValue = this.formatIsoDate(last7);
      this.endDateValue = todayIso;
    } else if (preset === 'last30') {
      const last30 = new Date();
      last30.setDate(today.getDate() - 30);
      this.startDateValue = this.formatIsoDate(last30);
      this.endDateValue = todayIso;
    } else if (preset === 'custom') {
      if (!this.startDateValue || !this.endDateValue) {
        this.startDateValue = todayIso;
        this.endDateValue = todayIso;
      }
    }

    this.updateDateRangeControl();
  }

  onStartDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.value) return;
    this.startDateValue = input.value;
    if (this.endDateValue && this.startDateValue > this.endDateValue) {
      this.endDateValue = this.startDateValue;
    }
    this.detectDatePreset();
    this.updateDateRangeControl();
  }

  onEndDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.value) return;
    this.endDateValue = input.value;
    if (this.startDateValue && this.endDateValue < this.startDateValue) {
      this.startDateValue = this.endDateValue;
    }
    this.detectDatePreset();
    this.updateDateRangeControl();
  }

  openDatePicker(inputEl: HTMLInputElement): void {
    if (!inputEl) return;
    try {
      if (typeof inputEl.showPicker === 'function') {
        inputEl.showPicker();
      } else {
        inputEl.focus();
      }
    } catch (e) {
      // ignore
    }
  }

  detectDatePreset() {
    if (!this.startDateValue || !this.endDateValue) {
      this.selectedDatePreset = null;
      return;
    }
    const today = new Date();
    const todayIso = this.formatIsoDate(today);

    const last7 = new Date();
    last7.setDate(today.getDate() - 7);
    const last7Iso = this.formatIsoDate(last7);

    const last30 = new Date();
    last30.setDate(today.getDate() - 30);
    const last30Iso = this.formatIsoDate(last30);

    if (this.startDateValue === todayIso && this.endDateValue === todayIso) {
      this.selectedDatePreset = 'today';
    } else if (
      this.startDateValue === last7Iso &&
      this.endDateValue === todayIso
    ) {
      this.selectedDatePreset = 'last7';
    } else if (
      this.startDateValue === last30Iso &&
      this.endDateValue === todayIso
    ) {
      this.selectedDatePreset = 'last30';
    } else {
      this.selectedDatePreset = 'custom';
    }
  }

  updateDateRangeControl() {
    const fieldName = this.getDateRangeFieldName();
    const control = this.getControl(fieldName);
    if (this.startDateValue && this.endDateValue) {
      const startFormatted = this.formatDisplayDate(this.startDateValue);
      const endFormatted = this.formatDisplayDate(this.endDateValue);
      const label =
        this.startDateValue === this.endDateValue
          ? startFormatted
          : `${startFormatted} - ${endFormatted}`;

      if (control) {
        control.setValue(label, { emitEvent: true });
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      }

      const startCtrl = this.getControl('startDate');
      if (startCtrl) {
        startCtrl.setValue(this.startDateValue, { emitEvent: true });
        startCtrl.markAsDirty();
      }
      const endCtrl = this.getControl('endDate');
      if (endCtrl) {
        endCtrl.setValue(this.endDateValue, { emitEvent: true });
        endCtrl.markAsDirty();
      }
    } else {
      if (control) {
        control.setValue(null, { emitEvent: true });
        control.markAsDirty();
      }
      this.getControl('startDate')?.setValue(null);
      this.getControl('endDate')?.setValue(null);
    }
    this.cd.detectChanges();
  }

  clearDateRange() {
    this.selectedDatePreset = null;
    this.startDateValue = '';
    this.endDateValue = '';
    this.updateDateRangeControl();
  }

  syncDateRangeFromFormGroup() {
    const fieldName = this.getDateRangeFieldName();
    const startVal = this.getControl('startDate')?.value;
    const endVal = this.getControl('endDate')?.value;
    const dateRangeVal = this.getControl(fieldName)?.value;

    if (startVal && endVal) {
      this.startDateValue =
        typeof startVal === 'string'
          ? startVal.split('T')[0]
          : this.formatIsoDate(new Date(startVal));
      this.endDateValue =
        typeof endVal === 'string'
          ? endVal.split('T')[0]
          : this.formatIsoDate(new Date(endVal));
      this.detectDatePreset();
    } else if (dateRangeVal) {
      if (
        typeof dateRangeVal === 'object' &&
        dateRangeVal.startDate &&
        dateRangeVal.endDate
      ) {
        this.startDateValue = dateRangeVal.startDate;
        this.endDateValue = dateRangeVal.endDate;
        this.detectDatePreset();
      }
    }
  }

  // Visual helper methods
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return '#808290';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Beautiful selection of colors suitable for both light and dark theme
    const colors = [
      '#0052CC', // Blue
      '#00875A', // Green
      '#DE350B', // Red
      '#00B8D9', // Teal
      '#6554C0', // Purple
      '#FF5630', // Orange
      '#36B37E', // Light Green
      '#FFAB00', // Yellow
    ];
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  onApplyClick() {
    this.apply.emit();
    this.isOpen = false;
  }

  hasValidPhoto(photo: any): boolean {
    if (!photo) return false;
    if (typeof photo !== 'string') return false;
    const trimmed = photo.trim();
    return (
      trimmed !== '' &&
      trimmed !== 'null' &&
      trimmed !== 'undefined' &&
      trimmed !== 'none'
    );
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = './assets/media/icons/default-user-icon.png';
  }

  onClearAllClick() {
    this.clearAllFilters();
  }
}
